import React, { useState, useEffect } from 'react';
import authService from '../../services/auth';
import apiService from '../../services/api';

/**
 * PermissionWrapper Component
 * 
 * This component checks if the user has a specific permission
 * and either renders the protected content or hides it.
 * 
 * Usage:
 * <PermissionWrapper permission="clients.create">
 *   <Button>Add Client</Button>
 * </PermissionWrapper>
 * 
 * Props:
 * - permission: string - The permission to check (format: "module.action")
 * - children: ReactNode - The content to render if permission is granted
 * - fallback: ReactNode - Optional fallback content to show if permission is denied
 * - hideIfDenied: boolean - Whether to hide the content if permission is denied (default: true)
 */
const PermissionWrapper = ({ 
  permission, 
  children, 
  fallback = null, 
  hideIfDenied = true 
}) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        setLoading(true);
        
        // If no permission is specified, grant access
        if (!permission) {
          setHasPermission(true);
          return;
        }

        const user = authService.getUser();
        if (!user) {
          setHasPermission(false);
          return;
        }

        // If user is admin or super admin, they have all permissions
        if (user.role_name?.toLowerCase() === 'admin' || user.role_name?.toLowerCase() === 'super admin') {
          setHasPermission(true);
          return;
        }

        // Check specific permission
        const response = await apiService.get(`/roles/user/${user.id}/permissions`);
        const permissions = response.data;
        
        const [module, action] = permission.split('.');
        const modulePermissions = permissions[module];
        
        if (!modulePermissions || !modulePermissions[action]) {
          setHasPermission(false);
        } else {
          setHasPermission(true);
        }
      } catch (error) {
        console.error('Permission check failed:', error);
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [permission]);

  // Show loading state while checking permissions
  if (loading) {
    return null; // or a loading spinner if you prefer
  }

  // If user has permission, render the children
  if (hasPermission) {
    return children;
  }

  // If user doesn't have permission and hideIfDenied is true, return null
  if (hideIfDenied) {
    return null;
  }

  // If user doesn't have permission and hideIfDenied is false, return fallback
  return fallback;
};

export default PermissionWrapper;
